import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { apiClient } from '../apiClient'
import { PROGRAM_PLAN_CAPACITY_METRICS_QUERY_KEY } from '../programs/training-modules/useGetMetrics'

type EligibilityPayload = {
  userIds: string[]
  programIds: string[]
}

export type SuspendLiveWorkPayload = EligibilityPayload & { message: string }

export type EligibilityUpdateResponse = {
  successCount: number
  failureCount: number
  failures: Array<{ userId: string; programId: string; reason: string }>
}

function invalidateTrainingMetrics(queryClient: QueryClient, programIds: string[]) {
  return Promise.all(
    programIds.map((programId) =>
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_PLAN_CAPACITY_METRICS_QUERY_KEY, programId],
      })
    )
  )
}

export function usePutTraining() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: EligibilityPayload) => {
      return await apiClient.put(`/int/v3/eligibility/training`, { ...payload })
    },
    onSuccess: (_data, payload) => invalidateTrainingMetrics(queryClient, payload.programIds),
  })
}

export function useDeleteTraining() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: EligibilityPayload) => {
      return await apiClient.delete(`/int/v3/eligibility/training`, { data: payload })
    },
    onSuccess: (_data, payload) => invalidateTrainingMetrics(queryClient, payload.programIds),
  })
}

export function usePutLiveWork() {
  return useMutation({
    mutationFn: async (payload: EligibilityPayload) => {
      return await apiClient.put<EligibilityUpdateResponse>(`/int/v3/eligibility/live-work`, payload)
    },
  })
}

export function useDeleteLiveWork() {
  return useMutation({
    mutationFn: async (payload: EligibilityPayload) => {
      return await apiClient.delete<EligibilityUpdateResponse>(`/int/v3/eligibility/live-work`, {
        data: payload,
      })
    },
  })
}

export function useDeleteSuperMajority() {
  return useMutation({
    mutationFn: async (payload: EligibilityPayload) => {
      return await apiClient.delete<EligibilityUpdateResponse>(`/int/v3/eligibility/super-majority`, {
        data: payload,
      })
    },
  })
}

export function useSuspendLiveWork() {
  return useMutation({
    mutationFn: async (payload: SuspendLiveWorkPayload) => {
      return await apiClient.put(`/int/v3/suspension/suspend`, { ...payload })
    },
  })
}

export function useResumeLiveWork() {
  return useMutation({
    mutationFn: async (payload: EligibilityPayload) => {
      return await apiClient.put(`/int/v3/suspension/resume`, { ...payload })
    },
  })
}
